import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { AlertCircle, CheckCircle2, Info, X } from "lucide-react";

const ToastContext = createContext(null);
const TOAST_TIMEOUT_MS = 4500;

function toastIcon(type) {
  if (type === "error") return AlertCircle;
  if (type === "success") return CheckCircle2;
  return Info;
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismissToast = useCallback((id) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback(
    ({ title, message, type = "info", duration = TOAST_TIMEOUT_MS }) => {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      setToasts((current) => [...current, { id, title, message, type }]);

      if (duration > 0) {
        window.setTimeout(() => dismissToast(id), duration);
      }

      return id;
    },
    [dismissToast]
  );

  const api = useMemo(
    () => ({
      showToast,
      success: (message, title = "Success") => showToast({ type: "success", title, message }),
      error: (message, title = "Request Failed") =>
        showToast({ type: "error", title, message, duration: 6500 }),
      info: (message, title = "Note") => showToast({ type: "info", title, message }),
      dismissToast,
    }),
    [dismissToast, showToast]
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        className="fixed right-4 top-4 z-[10000] flex w-[min(92vw,24rem)] flex-col gap-3"
        aria-live="polite"
        aria-relevant="additions text"
      >
        {toasts.map((toast) => {
          const Icon = toastIcon(toast.type);
          const accent =
            toast.type === "error"
              ? "border-rose-200 bg-rose-50 text-rose-800"
              : toast.type === "success"
                ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                : "border-indigo-200 bg-indigo-50 text-indigo-800";

          return (
            <div
              key={toast.id}
              className={`rounded-xl border px-4 py-3 shadow-lg shadow-slate-900/10 ${accent}`}
              role={toast.type === "error" ? "alert" : "status"}
            >
              <div className="flex items-start gap-3">
                <Icon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />
                <div className="min-w-0 flex-1">
                  {toast.title ? (
                    <p className="text-sm font-bold leading-5">{toast.title}</p>
                  ) : null}
                  {toast.message ? (
                    <p className="mt-0.5 text-sm font-medium leading-5 opacity-90">{toast.message}</p>
                  ) : null}
                </div>
                <button
                  type="button"
                  onClick={() => dismissToast(toast.id)}
                  className="rounded-md p-1 opacity-70 transition hover:bg-white/70 hover:opacity-100"
                  aria-label="Dismiss notification"
                >
                  <X className="h-4 w-4" aria-hidden />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const value = useContext(ToastContext);
  if (!value) {
    throw new Error("useToast must be used within ToastProvider");
  }
  return value;
}
